// Combining two trips into one.
//
// A trip is not a row (TripIdentity.mjs), so "these two trips are actually one
// trip" is not an update to a join column -- it is a write to every leg or
// entry of both, giving them one name and one `tripGroupId`. Rename is the same
// shape and the same file's problem; this module owns the three questions
// Rename never had to ask:
//
//   which trip is next to this one   -> adjacentTrips
//   what identity do the two share   -> mergedTripIdentity
//   which records have to be written -> recordsToRefile
//
// Pure, and shared by the leg side (TaskPogoTrip legs, TripGroup.jsx) and the
// Captain's Log side (TaskPogoCompletedTask entries, LogbookTrip.jsx). Both
// hand it the same normalized ROW shape so neither has to know the other's
// storage:
//
//   { key, name, startDate, endDate }
//
// `key` is a tripGroupKey / entryTripKey; `name` is the first-seen spelling --
// which for an unnamed lone record is the app's own from-to title, and that is
// why callers carry a separate `named` flag rather than inferring one here.

import { legDays, tripDateMs } from './TripFields.mjs';
import {
  findTripGroup,
  newTripGroupId,
  tripIdentityDefaults,
} from './TripIdentity.mjs';
import { localDayIndex } from './TripGroups.mjs';

function startMs(row) {
  const t = new Date(row?.startDate ?? '').getTime();
  return Number.isNaN(t) ? null : t;
}

function endMs(row) {
  const t = new Date(row?.endDate ?? row?.startDate ?? '').getTime();
  return Number.isNaN(t) ? null : t;
}

// The order the trips list already puts trips in -- ascending by start date,
// which is sortTripGroups(groups, 'asc') -- plus the tie-breaks that list can
// afford to leave unspecified and this cannot.
//
// "The trip before this one" has to be ONE trip, so two trips starting the same
// day must not be able to swap places between renders: the shorter one comes
// first, and identical spans fall back to the key, which is unique.
export function compareTripRows(a, b) {
  const sa = startMs(a);
  const sb = startMs(b);
  if (sa !== sb) return sa - sb;
  const ea = endMs(a);
  const eb = endMs(b);
  if (ea !== eb) return ea - eb;
  return String(a?.key ?? '').localeCompare(String(b?.key ?? ''));
}

// The trips immediately before and after `key`, or null where there is none.
//
// The anchor is resolved with findTripGroup, never `.find(r => r.key === key)`:
// a legacy 'n:' URL -- a bookmark, a mailed link, a share row -- addresses a
// trip that carries an id now, and an exact-key lookup would answer "not in
// this list" and silently offer no buttons on exactly the trips a merge exists
// to repair.
//
// A row with no usable start date is dropped rather than sorted: "the trip
// before this one" is a question about dates, an undated trip cannot answer it
// either way, and a NaN in the comparator leaves the whole order unspecified.
// If the ANCHOR is the undated one, there are no neighbours at all.
export function adjacentTrips(rows, key) {
  const all = rows || [];
  const anchor = findTripGroup(all, key, {
    // An ambiguous legacy name key resolves newest-first, the order
    // TripGroups' own findTripGroup answers such a key in.
    compare: (found) => [...found].sort(compareTripRows).reverse(),
  });
  if (!anchor) return { previous: null, next: null };

  const sorted = all.filter((row) => startMs(row) !== null).sort(compareTripRows);
  const index = sorted.findIndex((row) => row.key === anchor.key);
  if (index < 0) return { previous: null, next: null };

  return {
    previous: sorted[index - 1] ?? null,
    next: sorted[index + 1] ?? null,
  };
}

// The identity every record of both trips takes.
//
// The SOURCE's id wins -- the trip the user is standing on and asked to pull
// another one into. That is what keeps the rule true that a bound trip does not
// re-address when it is edited, and only a legacy 'n:' one does. Preferring the
// target's id would move the page, strand this trip's own share link and
// weather subscription, and leave its 'g:' URL resolving to nothing, because a
// stale 'g:' key never falls back by name.
//
// Neither side bound -> a fresh id, deliberately: leaving the combined trip
// name-keyed would let it absorb any other trip that happens to share the name,
// which is the bug ids exist to prevent.
//
// A blank name THROWS rather than returning tripIdentityDefaults' `{}`. That
// empty object would go to every record as an empty payload, so the merge would
// report success having written nothing at all.
export function mergedTripIdentity({ sourceGroupId, targetGroupId, name } = {}) {
  const label = name == null ? '' : String(name).trim();
  if (label === '') {
    throw new Error('A combined trip needs a name.');
  }
  const id =
    String(sourceGroupId ?? '').trim()
    || String(targetGroupId ?? '').trim()
    || newTripGroupId();
  return tripIdentityDefaults({ name: label, tripGroupId: id });
}

// The records that actually have to be written: the ones whose stored name or
// id differs from the merged identity.
//
// The rule both renames apply inline ("legs already spelling the name exactly
// are left alone"), stated once because a merge asks it of two trips at a time
// and the target's records are usually already correct. `readIdentity` is a
// parameter because the leg side reads columns and the entry side reads the
// parsed `fields` blob.
export function recordsToRefile(records, identity, readIdentity) {
  const wantedName = String(identity?.name ?? '');
  const wantedId = String(identity?.tripGroupId ?? '');
  return (records || []).filter((record) => {
    const current = (readIdentity ? readIdentity(record) : record) || {};
    const name = current.name == null ? '' : String(current.name);
    const id = current.tripGroupId == null ? '' : String(current.tripGroupId);
    return name !== wantedName || id !== wantedId;
  });
}

// The merged legs in the order the trip will run in -- buildGroup's sort, so a
// caller doesn't have to restate it to ask the question below.
export function orderedMergedLegs(legs) {
  return [...(legs || [])].sort((a, b) => tripDateMs(a) - tripDateMs(b));
}

// The `days` writes that keep the combined leg chain honest at its SEAMS.
//
// A leg has no order column: its place in a trip is its date, and `days` is
// what dates the leg after it (cascadeLegDates). Two trips a month apart become
// one trip whose boundary leg still says `days: 1` with thirty days after it --
// and the next cascade at or before that leg (a Days edit, a re-date, a
// reorder, a delete) re-derives every later date from that 1 and yanks the
// absorbed trip a month earlier. Leaving it alone is not "changing nothing", it
// is arming a trap the user has no way to see.
//
// So only the SEAMS are fixed: a leg whose successor in the merged order came
// from the OTHER trip. Every other leg keeps the `days` its owner typed,
// including a mid-trip value a date edit already made disagree with its gap --
// cascadeAfterDateChange documents why that one is deliberate. The LAST leg is
// never touched: nothing follows it to measure against, and rewriting it would
// move the trip's throughDate and so its whole date range.
//
// `sideOf(leg)` returns anything comparable saying which trip a leg came from.
// Dates read through localDayIndex, which is the reading cascadeLegDates uses
// (it anchors on `dayjs(date).startOf('day')`) -- not the calendar's UTC one.
export function seamDaysUpdates(orderedLegs, sideOf) {
  const legs = orderedLegs || [];
  const updates = [];
  for (let i = 0; i < legs.length - 1; i += 1) {
    const leg = legs[i];
    const next = legs[i + 1];
    if (sideOf(leg) === sideOf(next)) continue;
    const from = localDayIndex(leg?.date);
    const to = localDayIndex(next?.date);
    if (from == null || to == null) continue;
    const days = Math.max(1, to - from);
    if (days === legDays(leg)) continue;
    updates.push({ id: leg.id, days });
  }
  return updates;
}
